import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  TextField,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  FormControlLabel,
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import LockIcon from '@mui/icons-material/Lock';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import PersonIcon from '@mui/icons-material/Person';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { PageHeader, SectionTitle, ConfirmDialog, FormSection, FormRow, useToast } from '../Components/ui';
import { radius } from '../theme';

// ─── Hilfskomponenten ─────────────────────────────────────────────────────────

const SectionCard = ({ icon: Icon, iconColor = 'primary.main', title, children }) => (
  <Box sx={{ border: 1, borderColor: 'divider', borderRadius: radius.xl, overflow: 'hidden' }}>
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        px: 2.5,
        py: 2,
        borderBottom: 1,
        borderColor: 'divider',
        backgroundColor: 'action.hover',
      }}
    >
      <Icon sx={{ color: iconColor, fontSize: 20 }} />
      <Typography variant="body1" fontWeight={700}>
        {title}
      </Typography>
    </Box>
    <Box sx={{ p: 2.5 }}>
      {children}
    </Box>
  </Box>
);

// ─── Hauptkomponente ──────────────────────────────────────────────────────────

const Settings = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');

  // E-Mail
  const [currentEmail,  setCurrentEmail]  = useState('');
  const [newEmail,       setNewEmail]       = useState('');
  const [emailPassword,  setEmailPassword]  = useState('');
  const [emailLoading,   setEmailLoading]   = useState(false);

  // Passwort
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword,      setNewPassword]      = useState('');
  const [confirmPassword,  setConfirmPassword]  = useState('');
  const [passwordLoading,  setPasswordLoading]  = useState(false);

  // Konto löschen
  const [deleteStep,        setDeleteStep]        = useState(0); // 0=zu, 1=erste Abfrage, 2=zweite Abfrage
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleteLoading,     setDeleteLoading]     = useState(false);

  // App-Einstellungen
  const [animationsEnabled, setAnimationsEnabled] = useState(
    localStorage.getItem('animationsEnabled') !== 'false'
  );

  const handleAnimToggle = (e) => {
    const val = e.target.checked;
    setAnimationsEnabled(val);
    localStorage.setItem('animationsEnabled', val ? 'true' : 'false');
  };

  // Feedback
  const { showToast } = useToast();
  const showMessage = (message, severity = 'success') => showToast(message, { severity });

  useEffect(() => {
    if (!token) return;
    fetch('/api/users/account', { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.ok ? r.json() : null)
      .then((d) => d && setCurrentEmail(d.email || ''))
      .catch(console.error);
  }, [token]);

  // ── E-Mail ändern ────────────────────────────────────────────────────────────
  const handleEmailUpdate = async (e) => {
    e.preventDefault();
    if (!newEmail.trim() || !emailPassword) {
      showMessage('Bitte neue E-Mail und aktuelles Passwort eingeben.', 'warning');
      return;
    }
    setEmailLoading(true);
    try {
      const res = await fetch('/api/users/update-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ newEmail: newEmail.trim(), password: emailPassword }),
      });
      if (res.ok) {
        const d = await res.json();
        setCurrentEmail(d.email);
        setNewEmail('');
        setEmailPassword('');
        showMessage('E-Mail-Adresse wurde aktualisiert.');
      } else if (res.status === 409) {
        showMessage('Diese E-Mail-Adresse wird bereits verwendet.', 'error');
      } else if (res.status === 403) {
        showMessage('Das eingegebene Passwort ist nicht korrekt.', 'error');
      } else {
        showMessage('E-Mail konnte nicht geändert werden.', 'error');
      }
    } catch {
      showMessage('Ein unerwarteter Fehler ist aufgetreten.', 'error');
    } finally {
      setEmailLoading(false);
    }
  };

  // ── Passwort ändern ──────────────────────────────────────────────────────────
  const handlePasswordUpdate = async (e) => {
    e.preventDefault();
    if (!currentPassword || !newPassword || !confirmPassword) {
      showMessage('Bitte alle Felder ausfüllen.', 'warning');
      return;
    }
    if (newPassword !== confirmPassword) {
      showMessage('Die neuen Passwörter stimmen nicht überein.', 'warning');
      return;
    }
    if (newPassword.length < 8) {
      showMessage('Das neue Passwort muss mindestens 8 Zeichen lang sein.', 'warning');
      return;
    }
    setPasswordLoading(true);
    try {
      const res = await fetch('/api/users/update-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ currentPassword, newPassword }),
      });
      if (res.ok) {
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
        showMessage('Passwort erfolgreich geändert.');
      } else if (res.status === 403) {
        showMessage('Dein aktuelles Passwort ist nicht korrekt.', 'error');
      } else {
        showMessage('Passwort konnte nicht geändert werden.', 'error');
      }
    } catch {
      showMessage('Ein unerwarteter Fehler ist aufgetreten.', 'error');
    } finally {
      setPasswordLoading(false);
    }
  };

  // ── Konto löschen ────────────────────────────────────────────────────────────
  const handleDeleteAccount = async () => {
    setDeleteLoading(true);
    try {
      const res = await fetch('/api/users/account', {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        localStorage.removeItem('token');
        navigate('/login');
      } else {
        setDeleteStep(0);
        showMessage('Konto konnte nicht gelöscht werden.', 'error');
      }
    } catch {
      setDeleteStep(0);
      showMessage('Verbindungsfehler.', 'error');
    } finally {
      setDeleteLoading(false);
    }
  };

  const closeDeleteDialog = () => {
    setDeleteStep(0);
    setDeleteConfirmText('');
  };

  return (
    <Container maxWidth="sm" sx={{ pt: 3, pb: 14, display: 'flex', flexDirection: 'column', gap: 4 }}>

      {/* Header */}
      <PageHeader
        title="Konto-Einstellungen"
        subtitle={<>Aktuell angemeldet als <strong>{currentEmail}</strong></>}
        sx={{ mb: 0, alignItems: 'flex-start' }}
      />

      {/* E-Mail */}
      <Box>
        <SectionTitle>Zugangsdaten</SectionTitle>
        <SectionCard icon={EmailIcon} title="E-Mail-Adresse ändern">
          <Box component="form" onSubmit={handleEmailUpdate} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormSection>
              <FormRow
                label="Neu"
                type="email"
                required
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                slotProps={{ autoCapitalize: 'none' }}
              />
              <FormRow
                label="Passwort"
                type="password"
                required
                value={emailPassword}
                onChange={(e) => setEmailPassword(e.target.value)}
              />
            </FormSection>
            <Button type="submit" variant="contained" disabled={emailLoading}>
              {emailLoading ? <CircularProgress size={20} color="inherit" /> : 'E-Mail aktualisieren'}
            </Button>
          </Box>
        </SectionCard>
      </Box>

      {/* Passwort */}
      <SectionCard icon={LockIcon} title="Passwort ändern">
        <Box component="form" onSubmit={handlePasswordUpdate} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <FormSection>
            <FormRow
              label="Aktuell"
              type="password"
              required
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
            />
            <FormRow
              label="Neu"
              type="password"
              required
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
            />
            <FormRow
              label="Bestätigen"
              type="password"
              required
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
            />
          </FormSection>
          <Typography variant="caption" color="text.secondary">Mindestens 8 Zeichen</Typography>
          <Button type="submit" variant="contained" disabled={passwordLoading}>
            {passwordLoading ? <CircularProgress size={20} color="inherit" /> : 'Passwort aktualisieren'}
          </Button>
        </Box>
      </SectionCard>

      {/* Profil-Link */}
      <Button
        variant="outlined"
        startIcon={<PersonIcon />}
        onClick={() => navigate('/profile')}
        sx={{ py: 1.5 }}
      >
        Körperdaten & Ziele bearbeiten
      </Button>

      {/* App-Einstellungen */}
      <Box>
        <SectionTitle>App-Einstellungen</SectionTitle>
        <SectionCard icon={AutoAwesomeIcon} title="Darstellung">
          <FormControlLabel
            control={
              <Switch
                checked={animationsEnabled}
                onChange={handleAnimToggle}
                color="primary"
              />
            }
            label={
              <Box>
                <Typography variant="body2" fontWeight={600}>Animationen</Typography>
                <Typography variant="caption" color="text.secondary">
                  Erfolgs-Animation beim Erfassen von Einträgen
                </Typography>
              </Box>
            }
            sx={{ width: '100%', mx: 0, justifyContent: 'space-between', flexDirection: 'row-reverse' }}
          />
        </SectionCard>
      </Box>

      {/* Gefahrenzone */}
      <Box>
        <SectionTitle>Gefahrenzone</SectionTitle>
        <SectionCard icon={DeleteForeverIcon} iconColor="error.main" title="Konto löschen">
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Löscht deinen Account und alle gespeicherten Daten dauerhaft. Diese Aktion kann nicht rückgängig gemacht werden.
          </Typography>
          <Button
            variant="outlined"
            color="error"
            fullWidth
            onClick={() => setDeleteStep(1)}
            sx={{ py: 1.5 }}
          >
            Konto unwiderruflich löschen
          </Button>
        </SectionCard>
      </Box>

      {/* ── Dialog Schritt 1: erste Warnung ────────────────────────────────── */}
      <ConfirmDialog
        open={deleteStep === 1}
        onClose={closeDeleteDialog}
        onConfirm={() => setDeleteStep(2)}
        title="Konto löschen?"
        message={
          <>
            Wenn du dein Konto löschst, werden <strong>alle deine Daten</strong> dauerhaft entfernt:
            <Box component="ul" sx={{ mt: 1.5, mb: 0, pl: 2.5 }}>
              {['Alle Kalorieneinträge', 'Deine eigenen Lebensmittel', 'Profildaten & Einstellungen', 'Streak & Verlauf'].map((item) => (
                <Typography key={item} component="li" variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                  {item}
                </Typography>
              ))}
            </Box>
          </>
        }
        confirmLabel="Ja, weiter"
        destructive
      />

      {/* ── Dialog Schritt 2: Bestätigungstext ─────────────────────────────── */}
      <Dialog open={deleteStep === 2} onClose={closeDeleteDialog} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: 'error.main' }}>
          Letzte Bestätigung
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Tippe <strong>LÖSCHEN</strong> in das Feld, um dein Konto endgültig zu löschen.
          </Typography>
          <TextField
            fullWidth
            placeholder="LÖSCHEN"
            value={deleteConfirmText}
            onChange={(e) => setDeleteConfirmText(e.target.value)}
            autoFocus
            slotProps={{ htmlInput: { autoCapitalize: 'characters' } }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button onClick={closeDeleteDialog} variant="outlined" sx={{ flex: 1 }}>
            Abbrechen
          </Button>
          <Button
            onClick={handleDeleteAccount}
            color="error"
            variant="contained"
            disabled={deleteConfirmText !== 'LÖSCHEN' || deleteLoading}
            sx={{ flex: 1 }}
          >
            {deleteLoading ? <CircularProgress size={20} color="inherit" /> : 'Endgültig löschen'}
          </Button>
        </DialogActions>
      </Dialog>

    </Container>
  );
};

export default Settings;
